import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { NavItem } from "@/config/navigation";
import { prefetchRoute } from "@/utils/routePrefetch";
import {
  isFamilyHubRoute,
  isNavSubmenuBranchActive,
  FAMILY_HUB_NAV_BASE,
} from "@/config/familyHubSidebarNav";
import {
  buildTooltipContent,
  getOpenNavHref,
  getPremiumUpgradeNavHref,
  hasSubmenu,
  shouldShowProBadge,
} from "./navButtonHelpers";
import { NavSimpleLink, NavSubmenuButton } from "./navButtonViewHelpers";

interface NavButtonProps {
  href: string;
  icon: LucideIcon;
  label: string;
  isCollapsed: boolean;
  onClick?: () => void;
  requiresPremium?: boolean;
  isMobile?: boolean;
  badgeCount?: number;
  submenu?: NavItem[];
  isPaid?: boolean;
  subscriptionLoading?: boolean;
}

/**
 * Nav Button.
 */
export function NavButton({
  href,
  icon: Icon,
  label,
  isCollapsed,
  onClick,
  requiresPremium = false,
  isMobile = false,
  badgeCount,
  submenu,
  isPaid: isPaidProp,
  subscriptionLoading: subscriptionLoadingProp,
}: Readonly<NavButtonProps>) {
  const subscriptionFromHook = useSubscriptionStatus({
    enabled: isPaidProp === undefined,
  });
  const isPaid = isPaidProp ?? subscriptionFromHook.isPaid;
  const subscriptionLoading = subscriptionLoadingProp ?? subscriptionFromHook.isLoading;
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!hasSubmenu(submenu)) return;

    const branchActive = isNavSubmenuBranchActive(submenu, location.pathname, location.search);
    const familyHubParentActive =
      href === FAMILY_HUB_NAV_BASE && isFamilyHubRoute(location.pathname);
    setIsExpanded(branchActive || familyHubParentActive);
  }, [location.pathname, location.search, submenu, href]);

  const targetHref = requiresPremium && !isPaid ? getPremiumUpgradeNavHref() : getOpenNavHref(href);
  const showProBadge = shouldShowProBadge(requiresPremium, isPaid, subscriptionLoading);
  const tooltipContent = buildTooltipContent(label, showProBadge);

  if (hasSubmenu(submenu) && !isCollapsed) {
    return (
      <NavSubmenuButton
        href={href}
        targetHref={targetHref}
        icon={Icon}
        label={label}
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        isExpanded={isExpanded}
        showProBadge={showProBadge}
        badgeCount={badgeCount}
        submenu={submenu}
        onClick={onClick}
        onPrefetch={() => prefetchRoute(href)}
        onToggleExpanded={() => setIsExpanded((value) => !value)}
      />
    );
  }

  return (
    <NavSimpleLink
      href={href}
      targetHref={targetHref}
      icon={Icon}
      label={label}
      isCollapsed={isCollapsed}
      isMobile={isMobile}
      showProBadge={showProBadge}
      badgeCount={badgeCount}
      tooltipContent={tooltipContent}
      onClick={onClick}
      onPrefetch={() => prefetchRoute(href)}
    />
  );
}
